import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../lib/api';

interface TimelineRequestItem {
  type: 'request';
  usageScope: 'request' | 'conversation';
  timestamp: string;
  startedAt?: string;
  messageText: string;
  conversationTitle: string | null;
  siteName: string;
  credits: number;
  stepCount: number;
  imageCount: number;
  summary: string | null;
}

interface TimelineCreditItem {
  type: 'credit';
  timestamp: string;
  description: string;
  credits: number;
}

type TimelineItem = TimelineRequestItem | TimelineCreditItem;

interface DailyUsageEntry {
  date: string;
  credits: number;
}

interface UsageHistoryResponse {
  items: TimelineItem[];
  total: number;
  hasMore: boolean;
  dailyUsage: DailyUsageEntry[];
  period: {
    start: string | null;
    end: string | null;
  };
}

async function fetchUsageHistory(page: number): Promise<UsageHistoryResponse> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const response = await fetch(
    `${API_URL}/api/v2/cloud/usage?page=${page}&limit=25&tz=${encodeURIComponent(tz)}`,
    { credentials: 'include' },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch usage history');
  }

  return response.json();
}

export function useUsageHistory(page: number = 1) {
  return useQuery({
    queryKey: ['usageHistory', page],
    queryFn: () => fetchUsageHistory(page),
    staleTime: 60_000,
  });
}

export type { TimelineItem, TimelineRequestItem, TimelineCreditItem, DailyUsageEntry, UsageHistoryResponse };
